<template>
    <flex-element
        :element-type="elementType"
        class-default-inline="f-justify-center f-flex mob:f-flex-col f-flex-row f-flex-wrap f-w-1/1"
    >
        <div v-if="hasData && !dataLoaded" class="collection-dynamic__section">
            <loading-icon :type="loaderStyle" />
        </div>

        <a-text-single-line
            v-if="showNoResults"
            v-bind="element(1)"
            class-default-inline="f-justify-center"
            @element-updated="elementUpdated"
        >
            No results were found
        </a-text-single-line>


        <placeholder v-if="!hasData" text="Please configure dynamic collection options" />
        <div
            v-if="dataLoaded"
            class="f-flex f-flex-wrap f-flex-grow"
            :class="gutterClasses"
        >
            <div v-for="(component, i) in dynamicElements" :key="component.id" :class="itemClasses">
                <component
                    :is="component.elementType"
                    class="is-static"
                    v-bind="component"
                    :index="i"
                />
            </div>
        </div>
    </flex-element>
</template>

<script>
import axios from 'axios';
import gql from 'graphql-tag';
import { mapState, mapMutations } from 'vuex';
import jsonpath from 'jsonpath';

import searchQuery from '@/graphql/queries/search.gql';
import searchQueryGrouped from '@/graphql/queries/searchGrouped.gql';
import searchLocationsQuery from '@/graphql/queries/SelectLocationsData.gql';
import selectDataQuery from '@/graphql/queries/SelectData.gql';
import googleApiUtilsMixin from '@/mixins/google-api-utils';
import { SET_COLLECTION_DATA, SHOW_ALERT } from '@/store/types/mutation-types';
import baseOrganism from '@/mixins/base-organism/base-organism-smart';
import { Rendering } from '@/data/data-models';
import CollectionItem from '@/components-user/smart/containers/collection-item';
import { processDataSources } from '@/sitecore/sitecore-data-interface';
import { ERROR, getComponent } from '@/sitecore/sitecore-data-service';
import listenOnRootMixin from '@/mixins/listen-on-root';
import { dynamicMapComponent, getDataNode, minimizeComponent } from '@/utils/flex-dynamic-data';
import LoadingIcon from '@/components/shared/loading/loading-icon';
import Placeholder from '@/components-user/placeholder';
import { walkObject } from '@/utils/object';
import { getJsonFromQueryString } from '@/utils/link';
import { generateClass, getClassDefinition } from '@/utils/tailwind-helper';

const ITEM_CLASS_ATTR = 'collection-item-classes';

export default {
    name: 'collection-dynamic',
    components: { CollectionItem, LoadingIcon, Placeholder },
    mixins: [baseOrganism, listenOnRootMixin, googleApiUtilsMixin],

    apollo: {
        dynamicData: {
            query() {
                if (this.premadeQuery === 'search'
                    || this.premadeQuery === 'manual') {
                    if (this.groupBy) {
                        return searchQueryGrouped;
                    }
                    return searchQuery;
                }

                if (this.premadeQuery === 'locations') {
                    return searchLocationsQuery;
                }

                if (this.premadeQuery === 'graphQL' && this.graphQLquery) {
                    return gql`${ this.graphQLquery }`;
                }
                return selectDataQuery;
            },
            variables() {
                if (this.premadeQuery === 'graphQL') {
                    return this.graphQLVariables;
                }

                return {
                    dataSourceId: this.datasourceValue,
                    keyword: this.queryTerm,
                    category: this.categoryTerm,
                    tags: this.tagsTerm,
                    sortBy: this.sortByTerm,
                    page: this.pageTerm,
                    pageSize: this.pageSizeTerm,
                    rootItem: this.rootItemTerm,
                    templateName: this.templateNameTerm,
                    dateFrom: this.dateFromTerm,
                    dateTo: this.dateToTerm,
                    pageOnly: this.pageOnlyTerm,
                    coordinates: this.zipCodeTerm ? this.coordinatesStr : '',
                    radius: this.radiusTerm,
                    customCriteria: this.getCustomParameters(),
                    locationCriteria: this.getLocationParameters(),
                    fieldsEqual: this.getFieldParameters(),
                    includeLocations: this.includeLocationsTerm,
                    facetOn: this.facetOn,
                    groupBy: this.groupBy,
                };
            },

            update(data) {
                if (this.isNestedDynamicCollection) {
                    this.dataLoaded = true;
                    return [];
                }
                if (this.premadeQuery === 'API') return [];

                this.dataLoaded = true;
                this.facets = data.search ? data.search.facets : [];
                this.searchData = data.search;
                if (this.premadeQuery === 'search' || this.premadeQuery === 'manual') {
                    this.totalResults = data && data.search && data.search.results ? data.search.results.totalCount : 0;
                    if (this.premadeQuery === 'search') {
                        if (data.groupedSearch) {
                            this.totalResults = data.groupedSearch.groupedResults
                                ? data.groupedSearch.groupedResults.totalCount : 0;
                            return data.groupedSearch.groupedResults ? data.groupedSearch.groupedResults.items : [];
                        }
                        return data.search && data.search.results ? data.search.results.items : [];
                    }

                    if (this.premadeQuery === 'manual' && data.search && data.search.results) {
                        const reOrderedItems = [];
                        if (this.selectedItemsTerm && this.selectedItemsTerm.length > 0) {
                            this.selectedItemsTerm.forEach((item) => {
                                const match = data.search.results.items.find(i => i.item.id === item.id.replaceAll('-', '').toUpperCase());
                                if (match) {
                                    reOrderedItems.push(match);
                                }
                            });

                            return reOrderedItems;
                        }
                    }

                    return data.search && data.search.results ? data.search.results.items : [];
                }

                if (this.premadeQuery === 'locations') {
                    return data.search && data.search.results ? data.search.results.items : [];
                }

                if (this.premadeQuery === 'graphQL') {
                    data = jsonpath.query(data, this.graphQLJsonPath);
                    this.totalResults = data ? data.length : 0;
                    return data ? data[0] : [];
                }

                this.totalResults = data.item ? data.item.children.length : 0;
                return data.item ? data.item.children : [];
            },

            skip() {
                return !this.premadeQuery || (this.premadeQuery === 'item' && !this.datasourceValue) || (this.zipCode && !this.coordinates);
            },
        },
    },

    props: {
        displayName: {
            type: String,
            default: 'Dynamic Collection',
        },

        dynamicDataMapper: {
            type: Array,
            default: null,
        },

        componentId: {
            type: String,
            default: '',
        },

        map: {
            type: Array,
            default: null,
        },
    },

    data() {
        return {
            itemType: '',
            selectedPremadeComponentId: '',
            selectedPremadeComponentData: null,
            query: '',
            premadeQuery: '',
            reactiveTrigger: '',
            totalResults: 0,
            pagesAtOnce: 5,
            dataLoaded: false,
            dynamicElements: [],
            // search
            rootItem: '/sitecore/content',
            searchTerm: '',
            tags: '', // TAGID|TAGID|TAGID (will do an AND filter)
            category: '',
            templateName: '',
            dateFrom: '',
            dateTo: '',
            page: 0,
            pageSize: 10,
            sortBy: 'B3A0F95291C848E6B040B9C03614D133', // ID of sortitem from /sitecore/system/Settings/Foundation/Search/Sort Options
            pageOnly: true,
            currentPages: [],
            radius: 10,
            zipCode: null,
            results: [],
            facets: [],
            coordinatesStr: '',
            includeLocations: false,
            searchData: null,
            apiQueryStringDefaults: [],

            dynamicDataMapperNew: [],
            mapData: [],

            facetOn: [],
            groupBy: '',

            selectedItems: [],
            ignoreQuerystring: false,

            loaderStyle: 'spinner',

            graphQLquery: '',
            graphQLVariables: '',
            graphQLJsonPath: '',
            graphQLObject: {},
        };
    },

    computed: {
        ...mapState({
            previewMode: state => state.previewMode,
            pageData: state => state.page.pageData?.pageData,
        }),

        datasourceValue() {
            return this.getElementData('dynamicDatasourceValue');
        },

        updatedElement() {
            const rendering = new Rendering({
                ...this.updatedBaseElement,
                renderingId: this.renderingId,
                renderingName: this.renderingName,
                elements: this.elementsNew,

                // shared layouts always converted to final to save
                renderingField: this.renderingField,
                dataSourceId: this.dataSourceId,
            });
            return rendering;
        },
        // api section
        apiID() {
            return this.getElementData('apiIDDynamic');
        },

        apiStartingItem() {
            return this.getElementData('apiStartingItemDynamic');
        },

        graphQLResultFormat() {
            return this.getElementData('graphQLResultFormatDynamic');
        },

        // search section
        rootItemTerm() {
            const defaultTerm = this.getElementData('rootitemTermDynamic');
            // eslint-disable-next-line no-unused-vars
            const rt = this.reactiveTrigger;
            return this.getQueryStringParameterValue('rootitem') || defaultTerm || this.rootItem;
        },

        queryTerm() {
            const defaultTerm = this.getElementData('queryTermDynamic');
            // eslint-disable-next-line no-unused-vars
            const rt = this.reactiveTrigger;
            return this.getQueryStringParameterValue('query') || defaultTerm || this.searchTerm;
        },

        tagsTerm() {
            // tags are stored as an array of objects (since we need to know both the ID and name of each)
            // ... not the "TAGID|TAGID|TAGID" format that GraphQL expects
            // ... this is why we need to format it here
            const defaultTerm = this.hasElementData('tagsTermDynamic')
                ? this.getElementData('tagsTermDynamic').map(t => t.id).join('|')
                : '';
            // eslint-disable-next-line no-unused-vars
            const rt = this.reactiveTrigger;
            return this.getQueryStringParameterValue('tags') || defaultTerm || this.tags;
        },

        categoryTerm() {
            const defaultTerm = this.getElementData('categoryTermDynamic');
            // eslint-disable-next-line no-unused-vars
            const rt = this.reactiveTrigger;
            return this.getQueryStringParameterValue('category') || defaultTerm || this.category;
        },

        templateNameTerm() {
            const defaultTerm = this.getElementData('templateNameTermDynamic');
            // eslint-disable-next-line no-unused-vars
            const rt = this.reactiveTrigger;
            return this.getQueryStringParameterValue('templatename') || defaultTerm || this.templateName;
        },

        dateFromTerm() {
            const defaultTerm = this.getElementData('dateFromTermDynamic');
            // eslint-disable-next-line no-unused-vars
            const rt = this.reactiveTrigger;
            return this.getQueryStringParameterValue('datefrom') || defaultTerm || this.dateFrom;
        },

        dateToTerm() {
            const defaultTerm = this.getElementData('dateToTermDynamic');
            // eslint-disable-next-line no-unused-vars
            const rt = this.reactiveTrigger;
            return this.getQueryStringParameterValue('dateto') || defaultTerm || this.dateTo;
        },

        pageTerm() {
            const defaultTerm = this.getElementData('pageTermDynamic');
            // eslint-disable-next-line no-unused-vars
            const rt = this.reactiveTrigger;
            const pageNum = Number(this.getQueryStringParameterValue('page'));
            return Number(pageNum > 0 ? pageNum - 1 : pageNum || defaultTerm || this.page);
        },

        pageSizeTerm() {
            const defaultTerm = this.getElementData('pageSizeTermDynamic');
            // eslint-disable-next-line no-unused-vars
            const rt = this.reactiveTrigger;
            return Number(this.getQueryStringParameterValue('pageSize') || defaultTerm || this.pageSize);
        },

        sortByTerm() {
            const defaultTerm = this.getElementData('sortByTermDynamic');
            // eslint-disable-next-line no-unused-vars
            const rt = this.reactiveTrigger;
            return this.getQueryStringParameterValue('sortBy') || defaultTerm || this.sortBy;
        },

        pageOnlyTerm() {
            // eslint-disable-next-line no-unused-vars
            const rt = this.reactiveTrigger;

            const qp = this.getQueryStringParameterValue('pageOnly');
            if (qp) {
                if (qp === 'true') return true;
                return false;
            }

            if (this.hasElementData('pageOnlyTermDynamic')) {
                return this.getElementData('pageOnlyTermDynamic');
            }

            return this.pageOnly;
        },

        zipCodeTerm() {
            const defaultTerm = this.getElementData('zipCodeTermDynamic');
            // eslint-disable-next-line no-unused-vars
            const rt = this.reactiveTrigger;
            const zip = this.getQueryStringParameterValue('zip') || defaultTerm || this.zipCode;
            this.getCoordinates(zip);
            return zip;
        },

        radiusTerm() {
            const defaultTerm = this.getElementData('radiusTermDynamic');
            // eslint-disable-next-line no-unused-vars
            const rt = this.reactiveTrigger;
            return this.getQueryStringParameterValue('radius') || defaultTerm || this.radius;
        },

        includeLocationsTerm() {
            const defaultTerm = this.getElementData('includeLocations');
            // eslint-disable-next-line no-unused-vars
            const rt = this.reactiveTrigger;
            return defaultTerm || this.includeLocations;
        },

        selectedItemsTerm() {
            const defaultTerm = this.getElementData('selectedItemsTermDynamic');
            // eslint-disable-next-line no-unused-vars
            const rt = this.reactiveTrigger;
            return defaultTerm || this.selectedItems;
        },

        ignoreQuerystringTerm() {
            const defaultTerm = this.getElementData('ignoreQuerystringTermDynamic');
            // eslint-disable-next-line no-unused-vars
            const rt = this.reactiveTrigger;
            return defaultTerm || this.ignoreQuerystring;
        },

        hasData() {
            return this.selectedPremadeComponentId;
        },

        itemClasses() {
            const flexClassCollection = this.getElementData(ITEM_CLASS_ATTR) || {};
            const flexClassArray = [];

            walkObject(flexClassCollection, (obj, key, val) => {
                if (val && typeof val === 'string') flexClassArray.push(val);
            });

            return flexClassArray;
        },

        gutterClasses() {
            if (!this.itemClasses || this.itemClasses.length < 1) return null;

            const gutterProp = 'padding';
            const wrapperProp = 'margin';
            const gutterClasses = [];

            this.itemClasses.forEach((itemClass) => {
                const classDef = getClassDefinition(itemClass);
                if (classDef.tailwindProp === gutterProp) {
                    gutterClasses.push(generateClass({
                        screen: classDef.screen,
                        tailwindDefinitionProp: wrapperProp,
                        value: classDef.key * -1 + classDef.unit,
                    }));
                }
            });

            return gutterClasses;
        },

        isNestedDynamicCollection() {
            return !!this.dynamicDataMapperNew || !this.premadeQuery;
        },

        dynamicDataMapperComputed() {
            return this.dynamicDataMapperNew || this.getElementData('dynamicDataMapper') || [];
        },

        showNoResults() {
            if (this.isNestedDynamicCollection) return false;

            // show if data load and
            // is editor mode and data is configured
            // or if there are no results
            return this.dataLoaded && ((!this.previewMode && this.hasData)
                || (this.dynamicElements.length === 0));
        },
    },

    watch: {
        selectedPremadeComponentId(val) {
            if (this.getElementData('selectedPremadeComponentId') === val
                || this.isNestedDynamicCollection) {
                return;
            }
            this.setElementData('selectedPremadeComponentId', val);
        },

        premadeQuery(val) {
            if (this.getElementData('premadeQuery') === val) return;
            this.setElementData('premadeQuery', val);
        },

        query(val) {
            if (this.getElementData('query') === val) return;
            this.setElementData('selectedPremadeComponentId', val);
        },

        includeLocations(val) {
            if (this.getElementData('includeLocations') === val) return;
            this.setElementData('includeLocations', val);
        },
    },

    async created() {
        if (!this.ignoreQuerystringTerm) {
            this.listenOnRoot('flex::collection::query-refresh', this.refreshQuery);
        }
        this.listenOnRoot('flex::collection::set-collection-data', this.setCollection);
        this.loaderStyle = this.getElementData('loaderStyle') || 'spinner';

        this.dynamicDataMapperNew = this.dynamicDataMapper;
        this.mapData = this.map;

        if (this.zipCodeTerm) {
            await this.resolveZipCode(this.zipCodeTerm);
        }

        this.selectedPremadeComponentId = this.componentId || this.getElementData('selectedPremadeComponentId');
        if (this.selectedPremadeComponentId) {
            this.premadeQuery = this.getElementData('premadeQuery');
            this.groupBy = this.getElementData('groupByDynamic');

            const facetString = this.getElementData('facetOnDynamic');
            if (facetString) {
                this.facetOn = facetString.split(',').map(i => i.trim());
            }
            this.apiQueryStringDefaults = this.getElementData('apiQueryStringDefaultsDynamic');
            this.graphQLObject = {
                query: this.getElementData('dynamicGraphQL'),
                variables: this.getElementData('dynamicGraphQLVariables'),
                graphQLJsonPath: this.getElementData('dynamicGraphQLResultFormat'),
                tokenDefaults: this.getElementData('dynamicGraphQLVariableDefaults'),
                ignoreQuerystring: this.getElementData('ignoreQuerystringTermDynamic'),
            };
            await this.setCollection();
        }
    },

    methods: {
        ...mapMutations({
            setCollectionData: SET_COLLECTION_DATA,
            showAlert: SHOW_ALERT,
        }),

        async refreshQuery() {
            // used to hack reactive computed properties since they don't update from querystring
            this.reactiveTrigger = Date.now().toString();
            this.dynamicElements = [];
            await this.setCollection();
        },

        async getCoordinates(zip) {
            if (zip) {
                await this.resolveZipCode(zip);
                if (this.coordinates) {
                    this.coordinatesStr = `${ this.coordinates.lat },${ this.coordinates.lng }`;
                }
            }
            return '';
        },

        generateElements(dataSet) {
            if (!this.selectedPremadeComponentData || !dataSet) return;

            const elements = [];
            dataSet.forEach((child) => {
                const pointer = elements.length + 1;
                const element = this.dataMapper(child, pointer, this.selectedPremadeComponentData);
                elements.push(element);
            });
            this.dynamicElements = elements;
        },

        async setCollection(
            collectionId = this.id,
            componentId = this.selectedPremadeComponentId,
            premadeQuery = this.premadeQuery,
            searchObject = {},
            queryStringObject = this.apiQueryStringDefaults,
            graphQLObject = this.graphQLObject,
        ) {
            if (collectionId !== this.id) return;

            this.premadeQuery = premadeQuery;
            if (!this.isNestedDynamicCollection) {
                this.setCollectionData(null);
            }

            this.selectedPremadeComponentId = componentId;
            if (Object.keys(searchObject).length !== 0) {
                this.rootItem = searchObject.rootItem;
                this.searchTerm = searchObject.searchTerm;
                this.tags = searchObject.tags;
                this.category = searchObject.category;
                this.templateName = searchObject.templateName;
                this.dateFrom = searchObject.dateFrom;
                this.dateTo = searchObject.dateTo;
                this.pageSize = searchObject.pageSize;
                this.sortBy = searchObject.sortBy;
                this.pageOnly = searchObject.pageOnly;
                this.zipCode = searchObject.zipCode;
                this.radius = searchObject.radius;
                this.facetOn = searchObject.facetOn;
                this.groupBy = searchObject.groupBy;
                this.selectedItems = searchObject.selectedItems;
            }
            this.graphQLquery = graphQLObject.query;
            this.graphQLVariables = this.getGraphQLVariablesObject(graphQLObject);
            this.graphQLJsonPath = graphQLObject.graphQLJsonPath;

            this.dataLoaded = false;
            const response = await getComponent(this.selectedPremadeComponentId);
            if (response.status === ERROR) {
                this.showAlert({ description: response.error });
                this.setCollectionData(null);
                this.emitOnRoot('flex::collection::fetch-complete');
                return;
            }
            this.preloadComponents(jsonpath.query(this.dynamicDataMapperComputed, '$..selectedPremadeComponentId'));

            this.selectedPremadeComponentData = processDataSources([response.data], this, 'minimal')[0];

            if (!this.isNestedDynamicCollection) {
                if (this.premadeQuery !== 'API') {
                    if (this.$apollo.queries.dynamicData) {
                        this.dataLoaded = false;
                        try {
                            await this.$apollo.queries.dynamicData.refetch();
                        } catch (ex) {
                            console.error(ex);
                        }
                    }

                    this.generateElements(this.dynamicData);

                    if (this.dynamicData && this.dynamicData.length) {
                        this.results = this.dynamicData[0];
                    } else {
                        this.results = []; // needed to clear the "SAMPLE DATA" section of the dynamic options modal
                    }
                } else {
                    const webString = process.env.VUE_APP_GRAPHQL_WEB;
                    const webKey = webString.substring(webString.indexOf('sc_apikey='));
                    let action = `/sitecore/api/ssc/flex/FlexForms/1/Submit?${ webKey }&FlexFormId=${ this.apiID }`;
                    // handle relative links
                    if (action.startsWith('/')) {
                        if (process.env.VUE_APP_ENV === 'public') {
                            action = window.location.hostname + action;
                        } else {
                            action = process.env.VUE_APP_CM_URL + action;
                        }
                    }
                    this.dataLoaded = false;
                    const apiResponse = await axios.post(action, this.getQueryStringData(queryStringObject), { withCredentials: true } );
                    const startItem = getDataNode(apiResponse.data, this.apiStartingItem);

                    this.generateElements(startItem);

                    this.results = startItem[0];
                    this.dataLoaded = true;
                }
                this.setCollectionData(this.results);
                this.collectionLoaded(collectionId, this.results);
                this.emitOnRoot('flex::collection::fetch-complete');
                this.emitOnRoot('flex::collection::search-data-facets', this.facets);
                this.emitOnRoot('flex::collection::search-properties', { pageSize: this.getElementData('pageSizeTermDynamic') });
                this.emitOnRoot('flex::collection::search-data', this.searchData);
                this.emitOnRoot('flex::collection::search-keywords', this.searchKeywords);
                this.emitOnRoot('flex::collection::search-totalResults', this.totalResults);
            } else {
                this.generateElements(this.mapData);
                this.results = this.mapData
                    ? this.mapData[0]
                    : {};
                this.dataLoaded = true;
                this.collectionLoaded(collectionId, this.results);
            }
        },

        dataMapper(data, pointer, componentTemplate) {
            minimizeComponent(componentTemplate, `${ this.elementType }-minimal`);
            const componentCopy = JSON.parse(JSON.stringify(componentTemplate));

            const valueMap = this.dynamicDataMapperComputed;
            const processedComponent = dynamicMapComponent(data, componentCopy, valueMap, this.premadeQuery);
            return processedComponent;
        },

        getCustomParameters() {
            const customParameters = [];
            if (!this.ignoreQuerystringTerm) {
                const urlParams = new URLSearchParams(window.location.search);
                urlParams.forEach((value, key) => {
                    if (key.indexOf('__') > -1) {
                        customParameters.push({ name: key, value });
                    }
                });
            }
            return customParameters;
        },

        getLocationParameters() {
            const locationParameters = [];
            if (!this.ignoreQuerystringTerm) {
                const urlParams = new URLSearchParams(window.location.search);

                urlParams.forEach((value, key) => {
                    if (key.indexOf('loc_') > -1) {
                        locationParameters.push({ name: key, value });
                    }
                });
            }
            return locationParameters;
        },

        getFieldParameters() {
            const fieldParameters = [];
            if (!this.ignoreQuerystringTerm) {
                const urlParams = new URLSearchParams(window.location.search);
                urlParams.forEach((value, key) => {
                    if (key.indexOf('loc_') === -1 && key.indexOf('__') === -1 && key !== 'zip' && key !== 'radius' && key !== 'coordinates' && key !== 'tags' && key !== 'category' && key !== 'page' && key !== 'query' && key !== 'pageSize') {
                        fieldParameters.push({ name: key, value });
                    }
                });
            }
            if (this.selectedItemsTerm && this.selectedItemsTerm.length > 0) {
                fieldParameters.push({ name: '_group', value: this.selectedItemsTerm.map(i => (i.id)).join(',').replaceAll('-', '') });
            }
            return fieldParameters;
        },

        // get querystring merge data
        // querystring will overwrite default properties unless locked
        getQueryStringData(qsDefaultValues) {
           let qsDataObject = {};
            if (this.ignoreQuerystringTerm) {
                return qsDataObject;
            }

            if (window.location.search) {
                qsDataObject = { ...getJsonFromQueryString(window.location.search) };
            }

            // Normally you would do { ...defaultValues, ...qsDataObject }
            // to let the query string overwrite the default value.
            // Instead, to allow default properties to be locked, we're going to navigate the defaultValues
            // and set the correct values
            if (qsDefaultValues?.length) {
                const mergedDefaults = qsDefaultValues.reduce((obj, nv) => {
                    obj[nv.name] = qsDataObject[nv.name] !== undefined && !nv.lock
                        ? qsDataObject[nv.name]
                        : nv.value;

                    return obj;
                }, {});

                // merge to include any missing qsDataObject properties
                // note this is backwards so that the locked properties' value are not overwritten
                qsDataObject = { ...qsDataObject, ...mergedDefaults };
            }

            return qsDataObject;
        },

        // preload nested component data so that nested component retrieval
        // aren't repeated by the number of collection items
        // getComponent action caches data in the vuex
        async preloadComponents(componentIds) {
            if (componentIds && componentIds.length) {
                await Promise.all(componentIds.map((i) => {
                    return getComponent(i);
                }));
            }
        },

        updateGraphQLVariableFieldsEqualFromQueryString(graphQLvariable) {
            if (!window.location.search) {
                return graphQLvariable;
            }

            // GraphQL Variable fieldsEqual - Empty Array or does not have Query String parameter
            // Convert Query String Key/Value and add to fieldsEqual Array
            const urlParams = new URLSearchParams(window.location.search);
            urlParams.forEach((value, key) => {
                if (!graphQLvariable.fieldsEqual.some(item => item.name === key)) {
                        graphQLvariable.fieldsEqual.push({ name: key, value });
                }
            });

            // GraphQL Variable fieldsEqual - Not Empty Array
            // Convert Query String JSON Object and update fieldsEqual
            const urlQueryStringParameterJson = getJsonFromQueryString(window.location.search);
            const graphQLVariablesJsonUpdated = graphQLvariable.fieldsEqual.filter((qsFileds) => {
               return qsFileds.value = urlQueryStringParameterJson[qsFileds.name];
            });
            graphQLvariable.fieldsEqual = graphQLVariablesJsonUpdated;
            return graphQLvariable;
        },

        // convert graphQL variable string to object
        getGraphQLVariablesObject(graphQLObject) {
            if (!graphQLObject?.variables) return {};
            // if there is no token or not in component editor
            if (!graphQLObject?.tokenDefaults
                || graphQLObject.tokenDefaults.length < 1
                || this.pageData?.id) {
                const graphQLVarialbleData = JSON.parse(this.replaceGraphQLTokens(graphQLObject.variables));
                if (!this.ignoreQuerystringTerm) {
                    return this.updateGraphQLVariableFieldsEqualFromQueryString(graphQLVarialbleData);
                }
                return graphQLVarialbleData;
            }

            // if in component editor
            let variableStr = graphQLObject.variables;
            graphQLObject.tokenDefaults.forEach(function (item) {
                variableStr = variableStr.replace(item.name, item.value);
            });

            if (!this.ignoreQuerystringTerm) {
                return this.updateGraphQLVariableFieldsEqualFromQueryString(JSON.parse(variableStr));
            }
            return JSON.parse(variableStr);
        },

        replaceGraphQLTokens(str) {
            if (!this.pageData) return str;

            return str.replace(/\$\$contextItem\$\$/g, this.pageData.id);
        },

        getQueryStringParameterValue(parameterName) {
            const urlParams = new URLSearchParams(window.location.search);
            if (this.ignoreQuerystringTerm) {
                return null;
            }
            return urlParams.get(parameterName);
        },

        collectionLoaded(collectionId, data) {
            this.$root.$emit('flex::collection::load', { collectionId, data });
        },
    },
};
</script>

<style lang="scss">
.collection-dynamic {
    &__section {
        padding: 20px;
    }
}
</style>
